import React, { useState, useCallback, useEffect, useMemo, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Maximize2, Minimize2, Languages, Loader2 } from 'lucide-react';
import { NodeType, NodeStatus, ImageGenerationMode } from '@/types';
import { useModelStore } from '@/stores/modelStore';
import { useAgentStore } from '@/stores/agentStore';
import { NODE_TYPE_CONFIG } from '@/config/nodeTypeConfig';
import { TOPAZ_MODEL_ID, UPSCALE_FACTORS, DEFAULT_UPSCALE_SUBMODEL, UPSCALE_PORTRAIT_SUBMODEL, DEFAULT_UPSCALE_STRENGTH } from '@/services/generationStrategies';
import { cancelNodeGeneration } from '@/hooks/useGenerationOrchestrator';
import { PromptEditor } from './editor/PromptEditor';
import { useNodeTranslate, isChatModel, pickTranslateModel, clampExpandPanelSize } from '../hooks';
import { ModelConfigEditor } from './editor/ModelConfigEditor';
import { AdvancedParamsEditor } from './editor/AdvancedParamsEditor';
import { GenerationPreview } from './editor/GenerationPreview';
import { getModelCost } from '@/utils/credits';

interface EditorPanelProps {
  nodeId: string;
  nodeType: NodeType;
  status: NodeStatus;
  prompt: string;
  model?: string;
  aspectRatio?: string;
  resolution?: string;
  duration?: number;
  imageCount?: number;
  videoCount?: number;
  upscaleStrength?: number;
  upscaleSubModel?: string;
  generateAudio?: boolean;
  inputAssets: { id: string; type: 'image' | 'video' | 'audio'; src: string }[];
  isOpen: boolean;
  currentImageMode?: ImageGenerationMode;
  onPromptChange: (value: string) => void;
  onPromptSubmit: () => void;
  onUpdate: (id: string, data: any) => void;
  onGenerate: () => void;
  onImageModeSwitch?: (mode: ImageGenerationMode) => void;
  onAddUploadNode?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const GLASS_PANEL = "bg-[#262626]/95 backdrop-blur-2xl border border-white/10 shadow-2xl rounded-2xl";

/** 高清子模型选项（§6.2）：与 UPSCALE_SUBMODEL_MAP 键同源，onClick 写入 data.upscaleSubModel */
const UPSCALE_SUBMODEL_OPTIONS = ['通用', '人像', '风景', '建筑', '动漫', '低分辨率修复', '文字/Logo'];

/** 分辨率就近回落：切到档位不兼容的模型时取数值最接近的档位（720p→768p 而非直接落到最低档），无数值档位（如 1k/2k）回退首选 */
const pickClosestResolution = (target: string, supported: string[]): string => {
  const parse = (r: string) => parseInt(r, 10);
  const targetVal = parse(target);
  if (Number.isNaN(targetVal)) return supported[0];
  let closest = supported[0];
  let closestDiff = Infinity;
  for (const r of supported) {
    const v = parse(r);
    if (Number.isNaN(v)) continue;
    const diff = Math.abs(v - targetVal);
    if (diff < closestDiff) {
      closestDiff = diff;
      closest = r;
    }
  }
  return closest;
};

const EditorPanelInner: React.FC<EditorPanelProps> = ({
  nodeId,
  nodeType,
  status,
  prompt,
  model,
  aspectRatio,
  resolution,
  duration,
  imageCount,
  videoCount,
  upscaleStrength,
  upscaleSubModel,
  generateAudio,
  inputAssets,
  isOpen,
  currentImageMode,
  onPromptChange,
  onPromptSubmit,
  onUpdate,
  onGenerate,
  onImageModeSwitch,
  onAddUploadNode,
  className = '',
  style,
}) => {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [currentMode, setCurrentMode] = useState<string>('');
  const isWorking = status === NodeStatus.WORKING;
  const isUpscaleMode = nodeType === NodeType.IMAGE_GENERATOR && currentImageMode === 'UPSCALE';
  const getModelsByMode = useModelStore((s) => s.getModelsByMode);
  const getModelById = useModelStore((s) => s.getModelById);

  // v1.4.24 对齐脚本面板（ScriptQuickInput v1.4.17/20/17）：右上角全屏按钮 → createPortal 到 body 的
  // fixed 居中展开面板（脱离画布缩放/平移坐标系），遮罩点击还原，右下角手柄拖拽调尺寸（min 520×360 / max 视口 92%）
  const [expanded, setExpanded] = useState(false);
  const [panelSize, setPanelSize] = useState(() => clampExpandPanelSize(800, 520));
  const panelSizeRef = useRef(panelSize);
  panelSizeRef.current = panelSize;

  // 面板关闭时复位展开态（防下次打开直接进全屏）；展开态下窗口缩小时兜底 clamp
  useEffect(() => {
    if (!isOpen) setExpanded(false);
  }, [isOpen]);

  useEffect(() => {
    if (!expanded) return;
    const onResize = () => setPanelSize((s) => clampExpandPanelSize(s.w, s.h));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [expanded]);

  // 放大子模型选择窗点击弹出（对齐脚本节点底部面板下拉）：原 hover 式（group-hover 纯 CSS）改为点击切换；
  // 外点关闭走 capture 阶段 mousedown（面板根 onMouseDown stopPropagation 会阻断冒泡，capture 先于 React 合成事件）
  const [upscaleSubModelOpen, setUpscaleSubModelOpen] = useState(false);
  const upscaleSubModelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!upscaleSubModelOpen) return;
    const onDown = (e: MouseEvent) => {
      if (upscaleSubModelRef.current && !upscaleSubModelRef.current.contains(e.target as Node)) {
        setUpscaleSubModelOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [upscaleSubModelOpen]);

  // 右下角手柄：mousedown 记起点 → window mousemove 按增量 clamp 更新尺寸 → mouseup 收尾（脚本面板同款）
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const { w: startW, h: startH } = panelSizeRef.current;
    const onMove = (ev: MouseEvent) => {
      setPanelSize(clampExpandPanelSize(startW + (ev.clientX - startX), startH + (ev.clientY - startY)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // v1.4.24 翻译模型独立取对话模型（model prop 是图像/视频模型不可复用）：agentStore + 三级回退
  // （精确 id grsai-gemini-3.1-flash-lite → 名称含 flash lite → 首个 chat 模型），无 UI 选择器
  const agentModels = useAgentStore((s) => s.availableModels);
  const ensureAvailableModels = useAgentStore((s) => s.ensureAvailableModels);
  useEffect(() => { ensureAvailableModels(); }, [ensureAvailableModels]);
  const chatModels = useMemo(() => agentModels.filter(isChatModel), [agentModels]);
  const translateModelId = useMemo(() => pickTranslateModel(chatModels)?.id || '', [chatModels]);

  // v1.4.24 翻译（中英互译·自动方向）：复用共享设施 useNodeTranslate（脚本面板同款，执行状态按节点 id
  // 存模块级 store、跨面板卸载存活），译文经 onPromptChange 写回节点草稿；翻译中禁用发送防原文误发
  const { translating, translate: handleTranslate } = useNodeTranslate({
    nodeId,
    text: prompt,
    modelId: translateModelId,
    onResult: onPromptChange,
  });

  const getModeForNodeType = (type: NodeType): string => {
    switch (type) {
      case NodeType.VIDEO_GENERATOR: {
        const videoMode = currentMode || (inputAssets.length > 0 ? 'omni' : 'txt2video');
        return videoMode;
      }
      case NodeType.AUDIO_GENERATOR: return 'audio';
      case NodeType.IMAGE_EDITOR: return 'img2img';
      case NodeType.IMAGE_GENERATOR: return currentImageMode === 'TXT2IMG' ? 'txt2img' : 'img2img';
      case NodeType.PANORAMA_GENERATOR: {
        const hasInput = inputAssets.length > 0;
        return currentMode || (hasInput ? 'img2panorama' : 'txt2panorama');
      }
      default: return 'txt2img';
    }
  };

  const mode = getModeForNodeType(nodeType);
  // 高清双向互漏过滤：UPSCALE 模式锁定 Topaz（§6.2 应对②），普通生图/编辑模式过滤之（防误选 422）
  const allModeModels = getModelsByMode(mode);
  const models = (isUpscaleMode
    ? allModeModels.filter(m => m.id === TOPAZ_MODEL_ID)
    : allModeModels.filter(m => m.id !== TOPAZ_MODEL_ID)
  ).map(m => ({ l: m.name, v: m.id }));
  const currentModel = model ? getModelById(model) : undefined;
  const modelSupportedResolutions: string[] = currentModel?.supportedResolutions || [];
  const modelDurationRange = currentModel?.durationRange;
  // 积分系统 P1：响应式订阅模型表，⚡预估随 /api/models 定价（creditsPerUse）更新
  const storeModels = useModelStore((s) => s.models);

  const handleUpdate = useCallback((id: string, data: any) => {
    if (data.model && data.model !== model) {
      const newModel = getModelById(data.model);
      const newSupported = newModel?.supportedResolutions || [];
      if (newSupported.length > 0 && resolution) {
        const currentRes = resolution.toLowerCase();
        const isSupported = newSupported.some(r => r.toLowerCase() === currentRes);
        if (!isSupported) {
          data.resolution = pickClosestResolution(resolution, newSupported);
        }
      }
      // 时长钳制：切到声明了 durationRange 的模型（如 minimax-h3 的 1~15s、1080p 上限 10s）时，节点遗留的越界时长拉回范围
      const range = newModel?.durationRange;
      if (range && typeof duration === 'number') {
        const byRes = range.maxByResolution;
        const res = data.resolution || resolution || '';
        const effMax = (byRes && (byRes[res] ?? byRes[res.toLowerCase()])) ?? range.max;
        if (duration < range.min || duration > effMax) {
          data.duration = Math.min(Math.max(duration, range.min), effMax);
        }
      }
    }
    onUpdate(id, data);
  }, [model, resolution, duration, getModelById, onUpdate]);

  // 存量纠正：当前模型声明了专属档位而节点分辨率不在其中（如历史 720p 遇上 minimax-h3）时自动就近纠正
  useEffect(() => {
    if (
      modelSupportedResolutions.length > 0 &&
      resolution &&
      !modelSupportedResolutions.some(r => r.toLowerCase() === resolution.toLowerCase())
    ) {
      onUpdate(nodeId, { resolution: pickClosestResolution(resolution, modelSupportedResolutions) });
    }
  }, [nodeId, model, resolution, modelSupportedResolutions, onUpdate]);

  // 存量纠正（时长）：当前模型声明了 durationRange 而节点时长越界（如历史 20s 遇上 minimax-h3，或切 1080p 越过 10s 上限）时自动钳回
  useEffect(() => {
    if (!modelDurationRange || typeof duration !== 'number') return;
    const byRes = modelDurationRange.maxByResolution;
    const res = resolution || '';
    const effMax = (byRes && (byRes[res] ?? byRes[res.toLowerCase()])) ?? modelDurationRange.max;
    if (duration < modelDurationRange.min || duration > effMax) {
      onUpdate(nodeId, { duration: Math.min(Math.max(duration, modelDurationRange.min), effMax) });
    }
  }, [nodeId, duration, resolution, modelDurationRange, onUpdate]);

  if (!isOpen) return null;

  // v1.4.24 PromptEditor 两态共用 props：内嵌默认 80/120px 自适应高度；展开态经 editorWrapClassName/editorStyle 撑满剩余高度
  const editorProps = {
    nodeId,
    nodeType,
    prompt,
    currentImageMode,
    inputAssets,
    isWorking,
    currentMode,
    onPromptChange,
    onPromptSubmit,
    onImageModeSwitch,
    onAddUploadNode,
    onUpdate,
    onSetCurrentMode: setCurrentMode,
    onSetIsInputFocused: setIsInputFocused,
    onGenerate,
  };

  // v1.4.24 展开面板标题：节点类型中文名（NODE_TYPE_CONFIG）+ upscale 后缀
  const panelTitle = `${NODE_TYPE_CONFIG[nodeType]?.name || '参数面板'}${isUpscaleMode ? ' · 高清放大' : ''}`;

  // v1.4.24 普通模式底部行两态复用（内嵌/展开同构）：左=模型/高级参数，右=翻译/⚡积分/发送 三件套（对齐脚本面板 footer 右组 gap-3）
  const normalBottomRow = (
    <div className="flex items-center justify-between px-1">
      <div className="flex items-center gap-2">
        <ModelConfigEditor
          nodeId={nodeId}
          model={model}
          models={models}
          onUpdate={handleUpdate}
        />
        <AdvancedParamsEditor
          nodeId={nodeId}
          nodeType={nodeType}
          aspectRatio={aspectRatio}
          resolution={resolution}
          duration={duration}
          imageCount={imageCount}
          videoCount={videoCount}
          upscaleStrength={upscaleStrength}
          generateAudio={generateAudio}
          generationMode={currentImageMode}
          modelSupportedResolutions={modelSupportedResolutions}
          modelDurationRange={modelDurationRange}
          onUpdate={handleUpdate}
        />
      </div>
      <div className="flex items-center gap-3">
        {/* v1.4.24 翻译：中英互译·自动方向，译文替换提示词草稿；翻译中转 Loader2 防重入 */}
        <button
          type="button"
          disabled={translating || !prompt.trim() || !translateModelId}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleTranslate();
          }}
          title={translateModelId ? '翻译（中英互译）' : '暂无可用翻译模型'}
          className="text-white/40 hover:text-white/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {translating ? <Loader2 size={16} className="animate-spin" /> : <Languages size={16} />}
        </button>
        {/* v0.35：⚡积分图标改用顶部积分胶囊同款 token-icon.svg（原 Zap）；单价 = 当前模型定价（未配置 = 0） */}
        <span
          className="flex items-center gap-1 text-[12px] text-white/40"
          title="本次生成预计消耗积分"
        >
          <img src="/icons/home/token-icon.svg" alt="" width={12} height={12} draggable={false} />
          {/* 预留三位数积分宽度（tabular-nums 等宽数字），位数变化不引起布局跳动；同名聚合模型按当前档位取价 */}
          <span className="min-w-[22px] tabular-nums text-left">{getModelCost(model, storeModels, resolution)}</span>
        </span>
        <GenerationPreview
          isWorking={isWorking}
          onGenerate={onGenerate}
          canGenerate={!!prompt.trim() && !translating}
          onCancel={() => cancelNodeGeneration(nodeId)}
        />
      </div>
    </div>
  );

  // v1.4.24 高清放大参数区两态复用（内嵌/展开同构）：行距 gap-3 → gap-2 对齐脚本面板 8px 节奏
  const upscaleParams = (
    <div className="flex flex-col gap-2">
      <div className="text-[13px] font-medium text-white">高清放大</div>
      <ModelConfigEditor
        nodeId={nodeId}
        model={model}
        models={models}
        onUpdate={handleUpdate}
        layout="vertical"
      />
      <div className="flex items-center justify-between gap-4">
        <span className="text-[12px] text-white w-12 shrink-0">模型</span>
        <div className="relative" ref={upscaleSubModelRef} style={{ width: '300px' }}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setUpscaleSubModelOpen((v) => !v);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.06] border border-white/[0.10] cursor-pointer transition-colors text-[12px] font-medium text-white hover:bg-white/10"
          >
            <span className="flex-1 text-left">{upscaleSubModel || DEFAULT_UPSCALE_SUBMODEL}</span>
            <ChevronDown size={12} className="text-white/40" />
          </button>
          {upscaleSubModelOpen && (
            <div className="absolute top-full right-0 pt-2 w-full z-[200]">
              <div className="bg-[#262626] border border-white/10 rounded-xl shadow-xl overflow-hidden">
                {UPSCALE_SUBMODEL_OPTIONS.map(s => (
                  <div
                    key={s}
                    onClick={() => {
                      onUpdate(nodeId, { upscaleSubModel: s });
                      setUpscaleSubModelOpen(false);
                    }}
                    className={`px-3 py-2 text-xs font-medium cursor-pointer hover:bg-white/10 ${(upscaleSubModel || DEFAULT_UPSCALE_SUBMODEL) === s ? 'text-white bg-white/5' : 'text-white/60'}`}
                  >
                    {s}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* §6.2/§11-R4：人脸修复强度滑块，仅人像档（face_enhancement 开启档）展示，映射 face_enhancement_strength */}
      {(upscaleSubModel || DEFAULT_UPSCALE_SUBMODEL) === UPSCALE_PORTRAIT_SUBMODEL && (
        <div className="flex items-center justify-between gap-4">
          <span className="text-[12px] text-white shrink-0">人脸修复强度</span>
          <div className="flex items-center gap-2" style={{ width: '300px' }}>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={upscaleStrength ?? DEFAULT_UPSCALE_STRENGTH}
              onChange={(e) => onUpdate(nodeId, { upscaleStrength: Number(e.target.value) })}
              className="flex-1 h-1 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-lg transition-all"
            />
            <span className="text-[10px] font-mono text-white/60 w-8 text-right tabular-nums">
              {Math.round((upscaleStrength ?? DEFAULT_UPSCALE_STRENGTH) * 100)}%
            </span>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        <span className="text-[12px] text-white w-12 shrink-0">放大倍数</span>
        <div className="flex gap-2" style={{ width: '300px' }}>
          {UPSCALE_FACTORS.map(n => (
            <button
              key={n}
              onClick={() => onUpdate(nodeId, { resolution: `${n}k` })}
              className={`flex-1 py-2 rounded-xl text-[12px] font-medium transition-all border ${
                (resolution || '2k') === `${n}k`
                  ? 'bg-white/20 text-white border-white/40 shadow-[0_0_8px_rgba(255,255,255,0.1)]'
                  : 'text-white/50 border-white/10 hover:border-white/20 hover:bg-white/5'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // v1.4.24 展开态：createPortal 到 body 的 fixed 居中面板（结构与脚本面板展开态同构）——
  // 标题行 + 大编辑区/参数区 + 同款底栏 + 右下角拖拽手柄；React 树仍挂在本组件内（尺寸 state 不丢）
  if (expanded) {
    return createPortal(
      <>
        {/* 背景遮罩：四周压暗 + 毛玻璃模糊（脚本面板 v1.4.20 同款），点击遮罩还原为内嵌底部面板 */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-md"
          style={{ zIndex: 89 }}
          onClick={() => setExpanded(false)}
        />
        <div
          className="nodrag flex flex-col rounded-2xl bg-[#262626]/[0.97] backdrop-blur-xl border border-white/10 shadow-2xl p-5"
          style={{
            position: 'fixed',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: panelSize.w,
            height: panelSize.h,
            zIndex: 90,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 text-[15px] font-medium text-white/90 truncate" title={panelTitle}>
              {panelTitle}
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(false);
              }}
              title="还原为底部面板"
              className="shrink-0 text-white/40 hover:text-white/70 transition-colors cursor-pointer"
            >
              <Minimize2 size={16} />
            </button>
          </div>

          {isUpscaleMode ? (
            <div className="flex-1 min-h-0 mt-3 overflow-y-auto panel-scrollbar">
              {upscaleParams}
            </div>
          ) : (
            <div className="flex-1 min-h-0 mt-3 flex flex-col">
              <PromptEditor
                {...editorProps}
                editorWrapClassName="flex-1 min-h-0"
                editorStyle={{ height: '100%', minHeight: 0, maxHeight: 'none' }}
              />
            </div>
          )}

          <div className="mt-3">
            {isUpscaleMode ? (
              <GenerationPreview
                isWorking={isWorking}
                onGenerate={onGenerate}
                isUpscaleMode
                onBackToImg2Img={onImageModeSwitch ? () => onImageModeSwitch('IMG2IMG') : undefined}
                onCancel={() => cancelNodeGeneration(nodeId)}
                credits={getModelCost(TOPAZ_MODEL_ID, storeModels)}
              />
            ) : (
              normalBottomRow
            )}
          </div>

          {/* 右下角拖拽手柄：nwse-resize，最大视口 92% */}
          <div
            onMouseDown={handleResizeStart}
            title="拖拽调整面板尺寸"
            className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize flex items-end justify-end p-1 text-white/30 hover:text-white/60 transition-colors"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M9 1 L1 9" />
              <path d="M9 5 L5 9" />
              <path d="M9 8 L8 9" />
            </svg>
          </div>
        </div>
      </>,
      document.body,
    );
  }

  return (
    <div
      className={`${GLASS_PANEL} relative p-3 flex flex-col gap-2 transition-all duration-300 nodrag nowheel ${className}`}
      style={style}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.stopPropagation()}
    >
      {/* v1.4.24 全屏按钮（对齐脚本面板右上角同款）：切换到 Portal 居中展开面板；PromptEditor 顶行均左对齐，右上角无重叠 */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded(true);
        }}
        className="absolute top-3 right-3 z-10 text-white/40 hover:text-white/70 transition-colors cursor-pointer"
        title="全屏"
      >
        <Maximize2 size={15} />
      </button>

      <PromptEditor {...editorProps} />

      {isUpscaleMode ? (
        <>
          {upscaleParams}
          <GenerationPreview
            isWorking={isWorking}
            onGenerate={onGenerate}
            isUpscaleMode
            onBackToImg2Img={onImageModeSwitch ? () => onImageModeSwitch('IMG2IMG') : undefined}
            onCancel={() => cancelNodeGeneration(nodeId)}
            credits={getModelCost(TOPAZ_MODEL_ID, storeModels)}
          />
        </>
      ) : (
        normalBottomRow
      )}
    </div>
  );
};

export const EditorPanel = memo(EditorPanelInner, (prevProps, nextProps) => {
  const prevAssets = prevProps.inputAssets;
  const nextAssets = nextProps.inputAssets;
  const assetsEqual = prevAssets === nextAssets || (
    Array.isArray(prevAssets) && Array.isArray(nextAssets) &&
    prevAssets.length === nextAssets.length &&
    prevAssets.every((a, i) => a.id === nextAssets[i].id && a.type === nextAssets[i].type && a.src === nextAssets[i].src)
  );

  return (
    prevProps.nodeId === nextProps.nodeId &&
    prevProps.nodeType === nextProps.nodeType &&
    prevProps.status === nextProps.status &&
    prevProps.prompt === nextProps.prompt &&
    prevProps.model === nextProps.model &&
    prevProps.aspectRatio === nextProps.aspectRatio &&
    prevProps.resolution === nextProps.resolution &&
    prevProps.duration === nextProps.duration &&
    prevProps.imageCount === nextProps.imageCount &&
    prevProps.videoCount === nextProps.videoCount &&
    prevProps.upscaleStrength === nextProps.upscaleStrength &&
    prevProps.upscaleSubModel === nextProps.upscaleSubModel &&
    prevProps.generateAudio === nextProps.generateAudio &&
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.currentImageMode === nextProps.currentImageMode &&
    assetsEqual
  );
});
